import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, ErrorHandler, Provider } from '@angular/core';

import { SgLogglyTracker } from './loggly.service';

/** An error with the same signature is sent to loggly at most once per window (it is always printed to the console). */
export const SG_ERROR_HANDLER_THROTTLE_MS = 60_000;
const MAX_TRACKED_SIGNATURES = 200;

/**
 * A Global error handler that act's as a bridge to loggly.
 *
 * - `HttpErrorResponse`s are NOT sent: in apps that register `SgHttpLoggingInterceptor` (lender, admin, borrower,
 *   lead, analyzer, odelia) it already logs every HTTP error (deliberately skipping 404s), so an unhandled one reaching
 *   here would be a duplicate. Apps without the interceptor (external, onboarding, new-admin) only get it in the console.
 * - Repeats are throttled per name + message + first stack frame. This is the ONLY dedupe layer: it stops an error
 *   thrown on every change-detection tick (e.g. a template binding on a null object) from flooding loggly, and
 *   collapses a failed navigation that is reported twice (AppModule's NavigationError subscription + the Router).
 *   The next send of a throttled signature carries `suppressedRepeats` so Loggly still shows how often it happened
 *   (repeats in a window that is never followed by another occurrence stay console-only).
 */
@Injectable()
export class SgGlobalErrorHandler extends ErrorHandler {
  // `lastError` is a WeakRef so the throttle never keeps an error (and whatever it references) alive.
  private readonly lastSent = new Map<string, { sentAt: number; suppressed: number; lastError?: WeakRef<object> }>();

  constructor(private logger: SgLogglyTracker) {
    super();
  }

  handleError(error: any): void {
    console.error(error);
    if (error instanceof HttpErrorResponse) {
      return;
    }
    const suppressedRepeats = this.throttle(error);
    if (suppressedRepeats === null) {
      return;
    }
    if (suppressedRepeats > 0) {
      this.logger.customError(error, (log) => (log.suppressedRepeats = suppressedRepeats));
    } else {
      this.logger.error(error);
    }
  }

  /**
   * Returns `null` when the error must not be sent (same signature already sent within the window), otherwise the
   * number of repeats that were suppressed in the previous window (0 if none).
   */
  private throttle(error: any): number | null {
    const signature = this.signature(error);
    const now = Date.now();
    const entry = this.lastSent.get(signature);
    if (entry && now - entry.sentAt < SG_ERROR_HANDLER_THROTTLE_MS) {
      // The same error object reported twice (a failed navigation reaches here via both AppModule's NavigationError
      // subscription and the Router) is one occurrence, not a repeat. Trade-off: one cached Error instance genuinely
      // re-thrown within the window is undercounted the same way.
      if (!(isObject(error) && entry.lastError?.deref() === error)) {
        entry.suppressed++;
      }
      entry.lastError = asWeakRef(error);
      return null;
    }
    if (!entry && this.lastSent.size >= MAX_TRACKED_SIGNATURES) {
      for (const [key, { sentAt }] of this.lastSent) {
        if (now - sentAt >= SG_ERROR_HANDLER_THROTTLE_MS) {
          this.lastSent.delete(key);
        }
      }
      // Hard cap: if 200+ distinct signatures are still live, evict the oldest (Map keeps insertion order).
      while (this.lastSent.size >= MAX_TRACKED_SIGNATURES) {
        this.lastSent.delete(this.lastSent.keys().next().value);
      }
    }
    const suppressed = entry?.suppressed ?? 0;
    this.lastSent.delete(signature); // re-insert so insertion order tracks the latest send
    this.lastSent.set(signature, { sentAt: now, suppressed: 0, lastError: asWeakRef(error) });
    return suppressed;
  }

  private signature(error: any): string {
    if (error instanceof Error || (typeof error?.message === 'string' && typeof error?.stack === 'string')) {
      const firstFrame = typeof error.stack === 'string' ? (error.stack.split('\n')[1] ?? '') : '';
      return `${error.name ?? ''}|${error.message}|${firstFrame}`;
    }
    if (error && typeof error === 'object') {
      try {
        return JSON.stringify(error).slice(0, 200);
      } catch {
        return Object.prototype.toString.call(error);
      }
    }
    return String(error);
  }
}

function isObject(value: unknown): value is object {
  return (typeof value === 'object' || typeof value === 'function') && value !== null;
}

function asWeakRef(value: unknown): WeakRef<object> | undefined {
  return isObject(value) ? new WeakRef(value) : undefined;
}

/**
 * Registers {@link SgGlobalErrorHandler} as the app's `ErrorHandler`.
 *
 * Must be provided in the ROOT injector (shell-app `AppModule`). Angular resolves `ErrorHandler` for uncaught
 * errors (e.g. thrown in template event listeners) from the root environment injector, so an override provided in a
 * lazy-loaded app module is silently ignored (WI 68072).
 */
export const SG_GLOBAL_ERROR_HANDLER_PROVIDER: Provider = { provide: ErrorHandler, useClass: SgGlobalErrorHandler };
